/**
 * The save, the undo stack and the lost-race recovery, as one thing the screen can hold.
 *
 * `usePlanDocument` owns READING the document; this owns WRITING it. They are separate hooks because
 * they have separate lifetimes — the week view renders long before anything is saveable, and every
 * screen state below `ready` needs the first without the second.
 *
 * ── Everything that produces a new tree goes through the query cache ─────────────────────────
 * A successful save and an adopted winner both land as `setQueryData`, never as a direct store
 * write. That is what keeps the cache and the store from holding different plans: `usePlanDocument`'s
 * load effect is the single path into the document, and it fires on any new `select` result whatever
 * produced it. Writing the store directly here would leave the cache holding the pre-save tree, and
 * the next mount of this route would quietly render it.
 *
 * ── The two outcomes of a lost race, and why there is no third ───────────────────────────────
 * Document-model ADR §4. A save is whole-tree replace under an opaque `baseVersion`, so retrying
 * under a refreshed token re-sends the tree as it stood BEFORE the winner's edit landed — erasing
 * that edit, and, once it included a deletion, resurrecting the row it deleted. So the trainer gets
 * exactly two: adopt the winning tree, or keep editing under a token that will conflict again. There
 * is no "save mine anyway" here and no value in this hook's interface that could become one.
 *
 * What this app adds over the phone's raw counts is WHAT changed, by name — `summariseConflict`
 * diffs the winner against the pristine baseline the store kept, so the list answers "what did the
 * other person do?" rather than "how do my unsaved edits differ from the server?".
 *
 * **A winner that changed nothing is adopted silently.** It happens whenever the concurrent write
 * touched only server-owned bookkeeping, and prompting for it would ask the trainer to arbitrate
 * between two identical trees.
 *
 * ── Unsaved work is guarded on the way OUT, by one mechanism rather than two ──────────────────
 * There is no autosave (execution ADR §6 withdrew the patch-replay design as unbuildable), so an
 * unsaved edit to a STORED plan lives only in this tab's memory. Leaving the route or closing the tab
 * destroys it with no warning and no recovery — the plan is refetched clean on the way back in. A
 * brand-new plan is the one exception: its whole document is mirrored to the device
 * (`entities/plan-draft/lib/unsavedPlanRecord.ts`), so a reload offers it back — but only on this
 * device and for a week, which is why leaving it is still asked about.
 *
 * `useBlocker` covers BOTH exits. Verified against the installed
 * `node_modules/@tanstack/react-router@1.170.18` rather than recalled, because the hook has three
 * overloads and two of them are deprecated: the current one takes an OPTIONS OBJECT
 * (`{ shouldBlockFn, enableBeforeUnload, disabled, withResolver }`), and the older
 * `useBlocker(fn, condition)` positional form still type-checks. `enableBeforeUnload` is not
 * decoration — `@tanstack/history` registers its own capturing `beforeunload` listener and consults
 * the same blocker list — so a hand-rolled second listener would be a duplicate handler racing the
 * library's for the same event. One mechanism, two exits, and nothing to keep in sync.
 *
 * **It blocks an EXIT, never a selection.** See `shouldBlockFn`: the builder's phase, open day and
 * dock host all live in the address, so a blocker that stopped every navigation stopped the trainer
 * looking around their own plan.
 *
 * ── `withResolver`, and the three answers a trainer actually has ─────────────────────────────
 * It used to be off, because the guard asked with `window.confirm` — so the answer was known before
 * `shouldBlockFn` returned and there was no pending state for a dialog to render. Three things were
 * wrong with that, and they compounded:
 *
 * · **The copy was wrong for half the cases it fired on.** One sentence, "Masz niezapisane zmiany w
 *   tym planie", said to a trainer on `/plans/nowy` whose plan has no changes because it has no
 *   server row at all. What leaving costs there is the whole program, not an edit.
 * · **It fired exactly when Zapisz was refused.** A brand-new plan is `dirty` from the moment it
 *   opens and unsaveable until it has an exercise, so the guard demanded a choice the trainer could
 *   not act on: "save first" was not available and the dialog did not say so.
 * · **It offered two outcomes and the trainer wanted the third.** `Zapisz i wyjdź` is what somebody
 *   leaving a plan with real work in it means, and a native confirm has nowhere to put it.
 *
 * So the resolver is on, the guard renders `shared/ui/ConfirmDialog`, and `leave` below carries the
 * whole prompt already worded — including which of the two bodies applies and whether the third
 * answer exists at all. `enableBeforeUnload` is untouched: the tab-close path cannot render a dialog
 * and the browser's own prompt is the right one there.
 *
 * **The guard itself is a DELIBERATE, owner-visible departure from the canon, and it stays.** KANON
 * draws no leave prompt anywhere in the builder — its specimens are plans that are already saved.
 * This app has no autosave (execution ADR §6), so leaving a stored plan unguarded loses its edits,
 * and a new plan's device copy is a crash recovery rather than a save — it is gone with the device,
 * the week or a sign-out. Removing the dialog means building real persistence first, not restoring
 * fidelity.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  endFirstSave,
  markFirstSaveStarted,
  planTemplateFullQueryKey,
  readPlanDocument,
  readPlanDraft,
  structuralSaveRoute,
  structuralSaveStep,
  readPlanDraftBaseline,
  redoPlanDraftEdit,
  savePlanTemplate,
  settleSavedNewPlan,
  summariseConflict,
  undoPlanDraftEdit,
  useCanRedoPlanDraft,
  useCanUndoPlanDraft,
  useIsPlanDraftDirty,
  useDeviceCopyHeld,
  useIsUnsavedNewPlan,
  useSaveBlockingPhaseName,
  usePlanDraftRevision,
  usePlanRecipient,
  type ConflictChange,
  type FirstSaveAttempt,
  type SavePlanOutcome,
  type StructuralConsentScope,
  type StructuralSaveStep,
} from '~/entities/plan-draft';
import type { TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse } from '~/shared/api/generated/model';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

type FullResponse = TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse;

const KEY = 'web:planBuilder';

/**
 * How many named changes the dialog lists before it summarises the rest.
 *
 * A concurrent edit can touch dozens of nodes, and a dialog that lists forty of them is a dialog
 * nobody reads — the trainer needs enough to recognise the work, not an audit log. The overflow line
 * says how many are hidden rather than trailing off, so the count is never understated.
 */
const MAX_LISTED_CHANGES = 8;

/** Everything the conflict dialog renders. Already worded — the dialog holds no `t` of its own. */
export interface ConflictPrompt {
  readonly title: string;
  readonly description: string;
  /** One line per change, coarse to fine. Never empty when this prompt exists. */
  readonly lines: readonly string[];
  /** "…i 4 dalsze zmiany", or `null` when nothing was elided. */
  readonly overflow: string | null;
  readonly adoptLabel: string;
  readonly keepEditingLabel: string;
}

/**
 * How long the "Zapisano" confirmation stays in the save region, in milliseconds.
 *
 * It has to LEAVE, or the bar would claim a save just happened for the rest of the session — and it
 * has to stay long enough for a screen reader to reach it in a `polite` queue that may already be
 * announcing something else. Four seconds is the common floor for a transient status message.
 */
const SAVED_NOTICE_MS = 4000;

/**
 * Everything the structural-consent dialog renders. Already worded — this hook holds the `t`, and
 * the dialog holds none.
 *
 * `ConfirmPrompt` rather than a shape of its own: the consent is one instance of the app's confirm,
 * not a second kind of dialog, and a private twin of the same four fields would drift the first
 * time either grew a field.
 */
export type StructuralConsentPrompt = ConfirmPrompt;

/**
 * The leave guard, already worded and already wired.
 *
 * One field on the state rather than a prompt plus three loose handlers, because which handler a
 * button gets is part of the DECISION and not the screen's to reassemble: `onConfirm` is the save on
 * a plan that can be saved and the discard on one that cannot, and a screen deciding that for itself
 * would be a second place the rule lives.
 *
 * `onAlternate` is `null` exactly when the plan cannot be saved right now — there is no third answer
 * then, and the dialog draws two.
 */
export interface LeaveGuard {
  readonly prompt: ConfirmPrompt;
  /** Save and then leave, or — when the plan cannot be saved — leave and lose it. */
  readonly onConfirm: () => void;
  /** Stay. Escape and the backdrop map here, because staying is the answer that costs nothing. */
  readonly onCancel: () => void;
  /** Leave and lose it, when `onConfirm` is the save. `null` when there is nothing to save. */
  readonly onAlternate: (() => void) | null;
}

/**
 * A save held at the consent dialog: what to send when the trainer agrees.
 *
 * The STEP is stored rather than re-derived on confirm, and that is the point of storing it:
 * `structuralSaveStep` is resolved against the tree as it stood when the trainer pressed Save, and
 * a tree edited behind an open dialog would produce a step the dialog never described.
 */
interface PendingStructuralSave {
  readonly step: Extract<StructuralSaveStep, { step: 'consent' }>;
}

/**
 * The consent dialog's words, by what the save is about to do.
 *
 * **No scope names a count, because nothing measures one.** The endpoint that counted the
 * occurrences a retile would move went with the sequence engine, so each sentence states WHAT moves
 * and says plainly that how many is only known after the save.
 *
 * The dates copy mirrors mobile's (`plans.builder.*RecalcBody`), including the clause that does the
 * most work in practice: **"Wykonane treningi zostają na swoich datach."** A trainer's first fear on
 * reading that their client's sessions move is that it rewrites history, and it does not — R7 makes
 * a logged workout immutable and R3 regenerates only what has not started.
 */
function consentPrompt(
  t: Translate,
  pending: PendingStructuralSave,
): StructuralConsentPrompt {
  const { scope, reason } = pending.step;

  if (scope === 'bookings') {
    return {
      title: t(`${KEY}.structural.title`),
      description: t(`${KEY}.structural.bookings`),
      confirmLabel: t(`${KEY}.structural.confirm`),
      cancelLabel: t(`${KEY}.structural.cancel`),
    };
  }

  // `reason` is non-null on both dates scopes — `structuralSaveStep` only nulls it for `bookings` —
  // and it is what names the dialog after the phase edit pending. The fallback exists for the type
  // rather than for a reachable state.
  const kind: StructuralConsentScope = scope;
  return {
    title: t(`${KEY}.structural.datesTitle.${reason ?? 'resizePhase'}`),
    description:
      kind === 'datesAndBookings'
        ? t(`${KEY}.structural.datesAndBookings`)
        : t(`${KEY}.structural.dates`),
    confirmLabel: t(`${KEY}.structural.confirm`),
    cancelLabel: t(`${KEY}.structural.cancel`),
  };
}

/**
 * The one refusal a trainer has to go and ACT on, in the two lengths the screen genuinely needs.
 *
 * One object rather than two parallel nullable strings, because there is one fact here: either the
 * tree is serialisable or it is not, and a shape where `summary` could be present while `reason` was
 * absent describes a state that cannot exist. Every consumer takes the length its surface can carry.
 *
 * The two lengths are not a style choice. The full sentence measures 583px, and the top bar is a
 * single row it shares with the plan's name, the chips, the history pair, the save region and three
 * controls — so drawn there in full it ellipsised mid-word ("…nie zapisze się na serw"), which reads
 * as a rendering fault rather than as an explanation. A sentence cut mid-word is worse than one that
 * was never started, so the bar states the FACT and the control that refuses carries the whole of it.
 */
export interface SaveBlock {
  /**
   * "Pusta faza «Fundament»" — what the top bar draws beside a Zapisz it has greyed out.
   *
   * Short enough to stand in the bar at 1280 without truncating, and it names the phase, which is
   * the only fact that locates the work in a twelve-phase plan.
   */
  readonly summary: string;
  /**
   * The whole sentence, including the consequence — what the refused control carries on its `title`
   * and in its `aria-describedby`, what the save region announces when a press is refused, and what
   * the leave prompt quotes. Nothing truncates it.
   */
  readonly reason: string;
}

export interface PlanSaveState {
  /**
   * There is unsaved work. Drives the canon's `save-state` dot, the Save button's live state and the
   * navigation blocker.
   *
   * It is the undo stack's emptiness OR an id-less plan, and the second half is not a special case:
   * a plan seeded by the create modal has never been stored, so it is unsaved by definition even
   * though nothing has been edited yet. Without it the first Save on every new plan would be
   * disabled as "pristine" and closing the tab would take the plan with no warning.
   */
  readonly dirty: boolean;
  /**
   * Why this tree cannot be sent at all, already worded — a phase with no exercise in it. `null`
   * when the plan is serialisable.
   *
   * Separate from `dirty`, because they refuse for opposite reasons: pristine means "there is
   * nothing to send", this means "there is something, and the server would reject it". A brand-new
   * plan is in the second state from the moment it opens, and telling the trainer that in the button
   * beats letting them press it and meet a bare 400.
   */
  readonly blocked: SaveBlock | null;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly saving: boolean;
  /** A refused save, already worded. `null` when the last attempt succeeded or none was made. */
  readonly error: string | null;
  /**
   * A transient, already-worded status line for the save region — "Zapisano" after a save lands, or
   * the note that a save cannot be undone. `null` most of the time.
   *
   * Separate from `error` because they are different facts with different urgency: this is `polite`
   * information about work that succeeded, and the banner is an `alert` about work that did not.
   * Folding them into one string would make the region's role a lie half the time.
   */
  readonly notice: string | null;
  /** Non-null exactly while the trainer is being asked to resolve a lost race. */
  readonly conflict: ConflictPrompt | null;
  /**
   * Non-null exactly while a save is waiting for consent to move the trainer's own bookings.
   *
   * Separate from `conflict` because they are different moments: a conflict is a save that already
   * failed, this is a save that has not been sent yet. Folding them into one prompt would mean one
   * of the two dialogs lying about which state the plan is in.
   */
  readonly structuralConsent: StructuralConsentPrompt | null;
  /**
   * Non-null exactly while a navigation OUT of this plan is being held for an answer. See
   * `LeaveGuard` and the header — the tab-close path is the browser's own prompt and is not this.
   */
  readonly leave: LeaveGuard | null;
  /**
   * Whether this plan is saved at all from here. `false` while the plan is an unsaved one being
   * built FOR a client (owner, 23 September 2026): the assignment is its save — the whole tree goes
   * to the client, and „Zapisz też jako szablon" puts it in the library too — so the bar offers no
   * Zapisz, its chord only announces that rule, and leaving offers no „Zapisz i wyjdź".
   */
  readonly offersSave: boolean;
  readonly save: () => void;
  /** Send the save the consent dialog was asked about. */
  readonly confirmStructural: () => void;
  /** Dismiss it. The plan stays exactly as edited, and unsaved. */
  readonly cancelStructural: () => void;
  readonly undo: () => void;
  readonly redo: () => void;
  /** Take the server's tree and discard the local edits. */
  readonly adoptWinning: () => void;
  /** Dismiss and keep editing. The stale token means the next save conflicts again. */
  readonly keepEditing: () => void;
}

type Translate = (key: string, values?: Record<string, string | number>) => string;

/**
 * A refusal → its sentence.
 *
 * Most refusals are one key and nothing else. A rule refusal is two facts — which exercise, and what
 * is wrong with its rule — and the inner one is already written in the rule bar's copy, so it is
 * resolved first and interpolated as `{{reason}}`. Composing rather than duplicating is what keeps
 * the bar's explanation and the save banner's from drifting: there is one sentence per refusal, and
 * both surfaces read it.
 */
function refusalMessage(
  t: Translate,
  outcome: Extract<SavePlanOutcome, { outcome: 'refused' }>,
): string {
  if (outcome.reason === undefined) return t(outcome.messageKey, outcome.messageValues);
  return t(outcome.messageKey, {
    ...outcome.messageValues,
    reason: t(outcome.reason.key, outcome.reason.values),
  });
}

/** One named change → one line. `plan` only ever reports as edited, so it has one key rather than three. */
function changeLine(t: Translate, change: ConflictChange): string {
  const name = change.name.trim();
  return t(`${KEY}.conflict.${change.node}.${change.kind}`, {
    name: name.length > 0 ? name : t(`${KEY}.conflict.unnamed`),
  });
}

/**
 * @param planId The stored template being edited, or `null` for a plan the create modal seeded and
 * nobody has saved yet. The two are told apart by `useIsUnsavedNewPlan`, never by this being null —
 * it is also null while no document is loaded at all.
 */
export function useSavePlanDocument(planId: number | null): PlanSaveState {
  const { t } = useTranslation();
  const contentLang = useContentLanguage();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const edited = useIsPlanDraftDirty();
  const emptyPhaseName = useSaveBlockingPhaseName();
  const isNewPlan = useIsUnsavedNewPlan();
  const deviceCopyHeld = useDeviceCopyHeld();
  const offersSave = usePlanRecipient() === null;
  const dirty = edited || isNewPlan;
  const canUndo = useCanUndoPlanDraft();
  const canRedo = useCanRedoPlanDraft();
  const revision = usePlanDraftRevision();

  /**
   * A refusal, together with the document revision it was ABOUT.
   *
   * Storing the pair is what lets the banner clear itself with no effect and no second source of
   * truth: a refusal describes one particular tree, so the moment the tree changes the message stops
   * being true, and `error` below simply stops deriving it.
   *
   * The banner used to persist until the NEXT save attempt. A trainer who read "nie można skrócić
   * fazy poniżej tygodni, które klient już przetrenował", undid the change and carried on working
   * kept a red strip over the top of a plan the message no longer described — through every
   * subsequent edit, including the ones that fixed it.
   *
   * Keyed on the revision rather than on `dirty`, which is a boolean already `true` by the second
   * edit and therefore useless as a change signal. It is derived during render rather than reset in
   * an effect, because an effect that calls `setState` synchronously is a cascading render and this
   * codebase lints for it — correctly: this was never state that needed storing twice.
   */
  const [refusal, setRefusal] = useState<{ readonly revision: number; readonly message: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [changes, setChanges] = useState<readonly ConflictChange[] | null>(null);
  const [winning, setWinning] = useState<FullResponse | null>(null);
  /**
   * The save waiting on a consent dialog, or `null`.
   *
   * It was a BOOLEAN, on the reasoning that `structuralSaveStep` could only ever answer
   * `scope: 'bookings'`. A phase resize or a phase copy dropped mid-plan re-dates a running client's
   * cycles, so all three scopes are live and the step has to survive until the trainer presses a
   * button.
   */
  const [pending, setPending] = useState<PendingStructuralSave | null>(null);
  /**
   * A save the LEAVE DIALOG asked for and did not get, already worded. `null` the rest of the time.
   *
   * Not folded into `error`: that one describes the tree and clears itself when the tree changes,
   * and this one describes an answer the trainer is still waiting for.
   */
  const [leaveError, setLeaveError] = useState<string | null>(null);

  /**
   * The refusal, but only while it still describes the document on screen.
   *
   * `notice` is deliberately NOT treated this way: "Zapisano" is about a save that really did
   * happen, and editing afterwards does not make it untrue. It expires on a timer instead.
   */
  const error = refusal !== null && refusal.revision === revision ? refusal.message : null;

  /** Word a refusal and bind it to the tree it is about. */
  const setError = (message: string | null) => {
    setRefusal(message === null ? null : { revision, message });
  };

  /** The confirmation and the undo note are both transient — one timer, cleared on unmount. */
  useEffect(() => {
    if (notice === null) return undefined;
    const timer = setTimeout(() => {
      setNotice(null);
    }, SAVED_NOTICE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [notice]);

  /**
   * Unsaved edits must survive neither a route change nor a tab close without the trainer being
   * asked. One registration covers both — see the header, and the installed router source. `disabled`
   * while clean means no blocker is registered at all, so a pristine plan navigates and reloads with
   * no prompt whatsoever.
   */
  /**
   * The ONE navigation this hook issues itself: `/plans/nowy` → `/plans/<newId>` after a first save.
   *
   * A ref rather than state, because it has to be readable inside `shouldBlockFn` at the moment the
   * router consults it — which is the same tick the navigation is requested in, before any re-render
   * could carry a new value into the blocker's closure. Without it the blocker would ask the trainer
   * to confirm leaving a plan it had itself just saved, one line after saving it.
   */
  const redirecting = useRef(false);

  /**
   * A save the trainer asked for on the way out.
   *
   * A ref rather than state for two reasons: it is read inside the mutation's `onSuccess`, which
   * fires long after the render that set it, and it must be readable by `adopt` in the same tick so
   * a first save can SKIP its own `/plans/nowy` → `/plans/<id>` redirect. Redirecting and then
   * resuming the held navigation would be two navigations racing for one address bar.
   */
  const leaveAfterSave = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      // **A selection change is not an exit.** The builder carries the phase, the open day and the
      // dock's host in the address (execution ADR §4), so opening a day, stepping to the next one or
      // expanding the dock are all NAVIGATIONS on the same pathname — and the blocker was stopping
      // every one of them the moment the plan went dirty. The visible symptom was a dock frozen on
      // whichever day it was opened on, with a confirm dialog nobody expected behind it, and it
      // reached PR-A1's phase tabs too: selecting a phase after an edit prompted.
      //
      // Compared on `pathname`, not on the search: leaving the plan is a different plan or a
      // different screen, and both change it.
      if (current.pathname === next.pathname) return false;
      if (redirecting.current) return false;
      // The answer is no longer known here. `withResolver` suspends the navigation and `leave`
      // below renders the question; `proceed` and `reset` are the two ways out of it.
      return true;
    },
    enableBeforeUnload: true,
    disabled: !dirty,
    withResolver: true,
  });

  /**
   * The blocker, readable from a callback that outlives the render it was created in.
   *
   * `blocker.proceed` is a fresh closure every render and the save that resumes a held navigation
   * lands one round trip later, so the mutation's `onSuccess` would otherwise call a stale one. The
   * write is in an EFFECT rather than in render: the React Compiler rejects a ref written during
   * render, and it is right to — a render that mutates is a render that cannot be replayed.
   */
  const blockerRef = useRef(blocker);
  useEffect(() => {
    blockerRef.current = blocker;
  });

  /**
   * Replace the document with a tree the server produced. One function for the three paths that do
   * it, because "write it to the cache and let the load effect take it from there" is the whole of
   * all three.
   *
   * ── A brand-new plan writes under the id the SERVER just minted ──────────────────────────
   * `planId` is null on `/plans/nowy`, so the key has to come from the response. The tree is written
   * first and the address changed second: by the time `/plans/$planId` mounts, its query is already
   * warm, so the builder opens on the plan with no spinner and no `#171` in the heading.
   *
   * ── A first save has to STAMP THE STORE, and skipping it posted the plan twice ────────────
   * Measured, on the leave path: create → add an exercise → `Zapisz i wyjdź` → browser Back. The
   * store still held the id-less seed, so `releasePlanDraft` kept it (that is its whole job), the
   * resumed navigation was a PUSH so `/plans/nowy` was still in history, and Back re-opened the
   * SAVED plan as an unsaved draft — Assign even said "najpierw zapisz plan". One press of Zapisz
   * posted a **second** program.
   *
   * Both halves are fixed here, and both are needed: the store is told the plan has a server row,
   * and `/plans/nowy` is REPLACED out of history on every first save rather than only on the path
   * that stays in the builder. On the leave path the held navigation is released afterwards
   * (`resumeAfterSave`), so the trainer still lands where they asked and Back lands on the plan they
   * just saved instead of on a draft that no longer exists.
   *
   * It is awaited rather than fired and forgotten, and that is what keeps the two navigations from
   * racing: `router.navigate` resolves after the location is committed, and only then is the blocked
   * push resumed. The intermediate replace is not itself blocked — `redirecting` makes
   * `shouldBlockFn` answer false, and the installed `useBlocker` returns before touching its
   * resolver when it does, so the dialog's pending answer survives untouched.
   */
  const adopt = async (response: FullResponse): Promise<void> => {
    const id = planId ?? response.id ?? null;
    if (id === null) return;
    queryClient.setQueryData(planTemplateFullQueryKey(id, contentLang), response);
    // The plan LIBRARY's row carries a changed-at date this save just moved — and, on a first save,
    // a whole row that did not exist. It is deliberately not invalidated from here: `pages` may not
    // import another `pages` slice, so reaching its query key would mean promoting one to `shared/`
    // for a single caller. It costs nothing — that read sets no `staleTime`, so returning to the
    // library refetches on mount and the list is already right by the time a trainer can see it.
    if (planId !== null) return;

    // The seed in the store is now a STORED plan and has to say so. Nothing else will do it on the
    // leave path: `usePlanDocument`'s load effect is what stamps the tree, and it only runs once
    // `/plans/$planId` has mounted — which, on the way out, may be for a single frame or not at all.
    // A tree carrying an id is also a tree `releasePlanDraft` is allowed to drop on unmount.
    //
    // The same act ends the device's recovery copy: the plan exists on the server now, and a record
    // outliving the save would offer it back as unsaved — one „Kontynuuj" from a second POST. A
    // response this app cannot represent still ends it, for the same reason, and takes the id-less
    // seed out of the store with it: left there, the next pagehide would write it back as unsaved.
    const read = readPlanDocument(response);
    if (!settleSavedNewPlan(read.outcome === 'draft' ? read.draft : null, firstSave.current)) {
      // The session changed while the POST was on the wire: the record is gone, and this tab now
      // belongs to somebody else, who is not carried to the previous trainer's plan.
      return;
    }

    redirecting.current = true;
    await navigate({ to: '/plans/$planId', params: { planId: String(id) }, replace: true });
  };

  /**
   * Release a navigation the guard is holding, once the save that was asked for has landed.
   *
   * Called on every settled outcome rather than only on success, and the flag is cleared either way:
   * a refused or failed save leaves the trainer exactly where they were, with the banner explaining
   * why — carrying them out of the plan on the strength of an intention that did not survive would
   * be the same data loss the guard exists to prevent.
   */
  const resumeAfterSave = (saved: boolean) => {
    if (!leaveAfterSave.current) return;
    leaveAfterSave.current = false;
    if (saved) blockerRef.current.proceed?.();
    else blockerRef.current.reset?.();
  };

  const dismissConflict = () => {
    setChanges(null);
    setWinning(null);
  };

  /**
   * The first save in flight, as `markFirstSaveStarted` captured it — which document, whose. A ref,
   * because the answer lands in callbacks long after the render that started it, and it must be
   * settled against the plan that was SENT, not whatever the tab holds by then.
   */
  const firstSave = useRef<FirstSaveAttempt | null>(null);

  const mutation = useMutation({
    mutationFn: async (commit: {
      readonly structuralConsent: boolean;
    }): Promise<SavePlanOutcome> => {
      const draft = readPlanDraft();
      // Guarded rather than asserted: the button is disabled while the document is absent, so
      // reaching here means a race between a click and an unmount, which is not an error.
      if (draft === null) return { outcome: 'refused', messageKey: `${KEY}.saveError.unknown` };
      // A new plan's device copy is marked BEFORE the POST leaves, and held until it is answered:
      // from here the server may already hold the plan, and a copy written now could come back as
      // unsaved and be POSTed twice. A no-op for a stored plan.
      firstSave.current = draft.id === null ? markFirstSaveStarted() : null;
      return savePlanTemplate({
        draft,
        lang: contentLang,
        structuralConsent: commit.structuralConsent,
      });
    },
    onMutate: () => {
      setError(null);
      dismissConflict();
      setPending(null);
    },
    onSuccess: async (outcome) => {
      if (outcome.outcome === 'saved') {
        // AWAITED. On a first save this also swaps `/plans/nowy` out of the address bar, and the
        // held navigation must not be resumed until that has committed — see `adopt`.
        await adopt(outcome.response);
        // The only SIGHTED confirmation a save gives is the unsaved dot disappearing — an absence,
        // which announces nothing. The save region is already a live region carrying "Niezapisane
        // zmiany", so replacing its text with "Zapisano" is the case that mechanism exists for.
        setNotice(t(`${KEY}.saved`));
        resumeAfterSave(true);
        return;
      }
      // Every answer below says the server stored nothing, so a new plan's copy drops its marker.
      endFirstSave(firstSave.current, 'refused');
      if (outcome.outcome === 'refused') {
        setError(refusalMessage(t, outcome));
        resumeAfterSave(false);
        return;
      }

      const baseline = readPlanDraftBaseline();
      const read = readPlanDocument(outcome.winning);
      if (baseline === null || read.outcome === 'refused') {
        // The winner exists but this app cannot represent it — a concurrent edit added a second
        // session to a day. There is nothing to diff and nothing safe to adopt, so the trainer is
        // told plainly rather than shown a dialog whose "adopt" would refuse the plan.
        setError(t(`${KEY}.saveError.conflictUnreadable`));
        resumeAfterSave(false);
        return;
      }

      const summary = summariseConflict(baseline, read.draft);
      if (!summary.changed) {
        await adopt(outcome.winning);
        resumeAfterSave(true);
        return;
      }
      // A lost race asks its own question, and the trainer has to answer that one before the one
      // about leaving. The held navigation is released rather than left dangling behind two dialogs.
      resumeAfterSave(false);
      setChanges(summary.changes);
      setWinning(outcome.winning);
    },
    onError: () => {
      // Only a non-`ApiError` reaches here — `savePlanTemplate` returns every modelled refusal as a
      // value. A transport fault is not something the backend said, so it gets the generic words.
      setError(t(`${KEY}.saveError.unknown`));
      resumeAfterSave(false);
    },
    // The one exit no path skips. A save that settled, or was refused, has already ended its
    // attempt and this does nothing; every other way out — a transport error, a success with no id
    // `adopt` can place — is an unanswered save: the marker stays and the writes resume.
    onSettled: () => {
      endFirstSave(firstSave.current, 'unanswered');
      firstSave.current = null;
    },
  });

  const hidden = changes === null ? 0 : Math.max(0, changes.length - MAX_LISTED_CHANGES);

  /**
   * The tree-level gate, asked BEFORE the press rather than relayed after it.
   *
   * `POST /plan-template` and `PATCH /plan-template/{id}` both refuse a phase carrying no exercise,
   * with a 400 whose message the trainer never sees, and a plan seeded by the create modal is in
   * exactly that state from the moment it opens. `firstEmptyPhaseName` names the phase — which is
   * the only useful fact when a twelve-phase plan has one empty block in the middle.
   *
   * The serialiser still refuses independently. This is the explanation; that is the guarantee.
   */
  const blocked: SaveBlock | null =
    emptyPhaseName === null
      ? null
      : {
          summary: t(`${KEY}.saveBlocked.noExerciseSummary`, { name: emptyPhaseName }),
          reason: t(`${KEY}.saveBlocked.noExercise`, { name: emptyPhaseName }),
        };

  /**
   * Save — after asking, when this save owes the trainer an answer about their own calendar or
   * their own bookings.
   *
   * The route is resolved from the CURRENT tree at the moment of the press, never from a render's
   * closure: the trainer may have undone the structural edit since the last render, and asking
   * about a change that is no longer in the document is how a confirmation stops being believed.
   *
   * ── One save raises at most ONE dialog ──────────────────────────────────────────────────
   * That is `structuralSaveStep`'s guarantee and this is where it is spent: the route is resolved
   * and the step that comes back is either a commit or exactly one consent. There is no shape of
   * this function that asks twice, and no round trip stands between the press and the dialog.
   */
  const runSave = () => {
    // ── The refusals, asked HERE because this is the only save ──────────────────────────────
    // The top bar's Zapisz has always been inert in each of these states, so nothing about the
    // button changes. What changed on 7 September 2026 is that the save gained a SECOND caller
    // with no button in front of it — Cmd/Ctrl+S, which a trainer reaches for from inside the day
    // panel, where the bar is behind a focus trap and `aria-hidden`. A shortcut that silently did
    // nothing there would read as a broken keybinding rather than as a rule, so each refusal is
    // worded into the region the trainer is already listening to.
    //
    // A dialog already in front of them is the exception, and it is the reason the two dialog
    // states answer with nothing: a consent prompt and a lost-race prompt are both modal and both
    // ask the question this press would repeat. Announcing over an open dialog would talk across
    // the answer it is waiting for.
    if (pending !== null || changes !== null) return;
    // A plan built for a client has no Zapisz: its save IS the assignment. The chord still reaches
    // here, and answers with that rule rather than with silence.
    if (!offersSave) {
      setNotice(t(`${KEY}.saveAtAssignReason`));
      return;
    }
    if (mutation.isPending) {
      setNotice(t(`${KEY}.saveInFlightReason`));
      return;
    }
    if (blocked !== null) {
      setNotice(blocked.reason);
      return;
    }
    if (!dirty) {
      setNotice(t(`${KEY}.savePristineReason`));
      return;
    }

    const draft = readPlanDraft();
    if (draft === null) {
      mutation.mutate({ structuralConsent: true });
      return;
    }

    const step = structuralSaveStep(structuralSaveRoute(draft));
    if (step.step === 'consent') {
      setPending({ step });
      return;
    }
    mutation.mutate({ structuralConsent: true });
  };

  /**
   * The leave guard's whole question, or `null` when nothing is being held.
   *
   * ── Two bodies, because the two situations cost different things ─────────────────────────────
   * A stored plan loses its unsaved EDITS. A plan the create modal seeded has never been anywhere
   * but this tab, so leaving loses the whole program — and the old single sentence, "Masz niezapisane
   * zmiany w tym planie", described the first while firing on both.
   *
   * ── Whether the third answer exists is a fact about the plan, not a preference ───────────────
   * `Zapisz i wyjdź` is offered exactly when a save would go through: the tree is serialisable and
   * there is something to send. A brand-new plan with no exercise fails that test — and that is
   * precisely when the old guard fired, so the trainer was asked to save and had no way to. Then the
   * dialog says why — `SaveBlock.reason`, the FULL sentence, because a prompt has the room the bar
   * does not and the trainer is being asked to act on it — and the way back is labelled after the
   * remedy rather than as a bare "stay".
   *
   * ── "A save is running" is NOT "this plan cannot be saved" ───────────────────────────────────
   * That test used to fold `!mutation.isPending` in, and this dialog is open THROUGH the save it
   * starts: pressing `Zapisz i wyjdź` re-rendered the same open dialog with the primary slot now
   * meaning DISCARD, at the same coordinates, with no busy state. A second click — a double click, a
   * held Enter — threw the work away. So the SHAPE of the dialog is a fact about the plan
   * (`saveable`) and being in flight is a separate `busy` the two leaving answers read.
   */
  const saveable = dirty && blocked === null && offersSave;
  /**
   * A save is in flight, so neither answer that LEAVES may be taken.
   *
   * Not narrowed to a save this dialog started. A save fired from the bar and then interrupted by a
   * click on the nav rail puts the trainer in the same place — work in flight, a dialog offering to
   * throw it away — and the answer is the same one.
   */
  const leavePending = mutation.isPending;

  // A new plan's leave promises the device's week only when the device HOLDS a copy — the last
  // write for this plan succeeded. Private mode, a full quota or no confirmed user leaves the plan in
  // this tab alone, and then leaving deletes it and the prompt says so, destructively.
  const keptOnDevice = isNewPlan && deviceCopyHeld;
  const leaveBody = !isNewPlan
    ? t(`${KEY}.leave.storedBody`)
    : keptOnDevice
      ? t(`${KEY}.leave.draftBody`)
      : t(`${KEY}.leave.draftBodyUnkept`);
  // Leaving a NEW plan the device holds throws nothing away: the record keeps it and the library's
  // „+ Nowy program" offers it back, so „Wyjdź i porzuć" would be a lie and the answer is „Wyjdź".
  // Without a device copy, leaving really does delete the plan, and the destructive answer returns.
  const leaveLabel = keptOnDevice ? t(`${KEY}.leave.draftLeave`) : t(`${KEY}.leave.discard`);
  const leave: LeaveGuard | null =
    blocker.status !== 'blocked'
      ? null
      : {
          prompt: {
            title: t(`${KEY}.leave.title`),
            // The body, then why a save is refused when it is, then why the save the trainer just
            // asked for did not happen. Each clause is present only when it is true.
            description: [
              leaveBody,
              saveable || blocked === null
                ? null
                : t(`${KEY}.leave.blocked`, { reason: blocked.reason }),
              leaveError,
            ]
              .filter((clause): clause is string => clause !== null)
              .join(' '),
            // The primary is the answer that keeps the work when there IS one, and the only leave
            // when there is not. Cancel is first in the DOM either way, so Enter on reflex stays.
            confirmLabel: saveable
              ? leavePending
                ? t(`${KEY}.saving`)
                : t(`${KEY}.leave.saveAndLeave`)
              : leaveLabel,
            cancelLabel:
              !saveable && blocked !== null
                ? t(`${KEY}.leave.stayAndFix`)
                : t(`${KEY}.leave.stay`),
            ...(saveable ? { alternateLabel: leaveLabel } : {}),
            // Nothing that leaves may be pressed while the save that would preserve the work is
            // still in flight. Staying is always allowed — it costs nothing and cancels nothing.
            ...(leavePending ? { busy: true } : {}),
            // When there is no save to offer, the primary is the one control on screen that
            // discards a stored plan's edits. It does not get the ember — see `ConfirmPrompt`. A new
            // plan's leave destroys nothing while the device holds it, so only then is it not
            // dressed as one.
            ...(saveable || keptOnDevice ? {} : { destructiveConfirm: true }),
          },
          onConfirm: () => {
            if (leavePending) return;
            if (!saveable) {
              leaveAfterSave.current = false;
              blocker.proceed?.();
              return;
            }
            // The navigation stays held. `resumeAfterSave` releases it when the save lands and puts
            // the trainer back on the plan when it does not.
            setLeaveError(null);
            leaveAfterSave.current = true;
            runSave();
          },
          onCancel: () => {
            // The intent does not outlive the dialog. Left set, it would make the NEXT ordinary
            // Zapisz try to release a guard that is no longer holding anything.
            leaveAfterSave.current = false;
            setLeaveError(null);
            blocker.reset?.();
          },
          onAlternate: saveable
            ? () => {
                if (leavePending) return;
                leaveAfterSave.current = false;
                setLeaveError(null);
                blocker.proceed?.();
              }
            : null,
        };

  return {
    dirty,
    blocked,
    canUndo,
    canRedo,
    // The preview round trip counts as saving: it is the first half of the press, and a Save button
    // that stays idle through it invites a second press that would start a second flow.
    saving: mutation.isPending,
    error,
    notice,
    conflict:
      changes === null
        ? null
        : {
            title: t(`${KEY}.conflict.title`),
            description: t(`${KEY}.conflict.description`),
            lines: changes.slice(0, MAX_LISTED_CHANGES).map((change) => changeLine(t, change)),
            overflow: hidden > 0 ? t(`${KEY}.conflict.more`, { count: hidden }) : null,
            adoptLabel: t(`${KEY}.conflict.adopt`),
            keepEditingLabel: t(`${KEY}.conflict.keepEditing`),
          },
    structuralConsent: pending === null ? null : consentPrompt(t, pending),
    leave,
    offersSave,
    save: runSave,
    confirmStructural: () => {
      // Whatever the step resolved to when the dialog was raised, unchanged. Re-deriving it here
      // would be a second chance to disagree with the sentence the trainer just read.
      const step = pending?.step;
      setPending(null);
      if (step === undefined) return;
      mutation.mutate({ structuralConsent: true });
    },
    cancelStructural: () => {
      setPending(null);
      // The save this consent was raised for is not happening, so an intent to leave once it landed
      // is not either. Left set, it outlives the act that created it: the leave dialog behind this
      // one is still open, and the next settled save — an ordinary Zapisz, minutes later — would try
      // to release a guard that has since been answered.
      leaveAfterSave.current = false;
    },
    /**
     * Undo, and say so when there is nothing to undo.
     *
     * A successful save discards the history (`loadPlanDraft` — an inverse patch addresses arrays by
     * index and the server may have reshaped the tree, so replaying one could write to a different
     * row than the trainer edited). The visible result is that Cmd+Z stops working right after a
     * save, silently, exactly when a trainer is most likely to press it — they saved, spotted a
     * mistake, and reached for undo. The keystroke does nothing and nothing explains why.
     */
    undo: () => {
      if (!canUndo) {
        setNotice(t(`${KEY}.undoPastSave`));
        return;
      }
      undoPlanDraftEdit();
    },
    redo: redoPlanDraftEdit,
    adoptWinning: () => {
      if (winning !== null) void adopt(winning);
      // Adopting REPLACES the tree, so any refusal about the tree that was there is now about a
      // document that no longer exists. The revision effect catches this too; clearing here as well
      // means the banner never survives even one frame past the act that invalidated it.
      setError(null);
      dismissConflict();
    },
    keepEditing: () => {
      setError(null);
      dismissConflict();
    },
  };
}
